import React, { useState, useEffect } from 'react';
import { CommonInput } from '@/components/ui/common-input';
import { CommonSelect } from '@/components/ui/common-select';
import { CommonButton } from '@/components/ui/common-button';
import { useToast } from '@/components/ui/use-toast';
import masterDataService from '@/services/masterDataService';
import { useAuth } from '@/contexts/AuthContext';

export const VEHICLE_TYPE_OPTIONS = [
  { value: 'Truck', label: 'Truck' },
  { value: 'Mini Trucks', label: 'Mini Trucks' },
  { value: 'LCV', label: 'LCV' },
  { value: 'HCV', label: 'HCV' },
  { value: 'Container Trucks', label: 'Container Trucks' },
  { value: 'Trailers & ODC', label: 'Trailers & ODC' },
];

const AddVehicleForm = ({ onSubmit, isSubmitting, onCancel }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    vehicle_number: '',
    vehicle_type: '',
    capacity: '',
    transporter_id: ''
  });
  const [errors, setErrors] = useState({});
  const [masterData, setMasterData] = useState({
    transporters: []
  });
  const [loading, setLoading] = useState({
    transporters: false
  });
  const { toast } = useToast();

  useEffect(() => {
  
    if (userProfile?.role === 'transporter' && userProfile?.id) {
      setFormData(prev => ({
        ...prev,
        transporter_id: userProfile.id
      }));
    }
    
    fetchMasterData();
  }, [userProfile]);

  const fetchMasterData = async () => {
    try {
      setLoading(prev => ({ ...prev, transporters: true }));
      const transporters = await masterDataService.getTransporters(0, 500, false);
      setMasterData(prev => ({ ...prev, transporters }));
    } catch (error) {
      console.error('Failed to fetch transporters:', error);
    } finally {
      setLoading(prev => ({ ...prev, transporters: false }));
    }
  };

  const transporterOptions = masterData.transporters.map(transporter => ({
    value: transporter.id,
    label: transporter.company_name || transporter.id
  }));

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.vehicle_number.trim()) {
      newErrors.vehicle_number = 'Vehicle number is required';
    }
    
    if (!formData.vehicle_type.trim()) {
      newErrors.vehicle_type = 'Vehicle type is required';
    }
    
    if (!formData.capacity) {
      newErrors.capacity = 'Capacity is required';
    } else if (isNaN(formData.capacity) || Number(formData.capacity) <= 0) {
      newErrors.capacity = 'Capacity must be a positive number';
    }
    

    if (userProfile?.role !== 'transporter' && !formData.transporter_id.trim()) {
      newErrors.transporter_id = 'Transporter is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const vehicleData = {
        vehicle_number: formData.vehicle_number,
        vehicle_type: formData.vehicle_type,
        capacity: Number(formData.capacity),
        transporter_id: userProfile?.role === 'transporter' ? userProfile.id : formData.transporter_id
      };
      
      await onSubmit(vehicleData);
    }
  };

  
  const showTransporterDropdown = userProfile?.role === 'ipl_super_admin';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CommonInput
          label="Vehicle Number"
          id="vehicle_number"
          value={formData.vehicle_number}
          onChange={(e) => handleChange('vehicle_number', e.target.value)}
          error={errors.vehicle_number}
          required
          placeholder="Enter vehicle number"
        />
        
        <CommonSelect
          label="Vehicle Type"
          id="vehicle_type"
          value={formData.vehicle_type}
          onValueChange={(value) => handleChange('vehicle_type', value)}
          options={VEHICLE_TYPE_OPTIONS}
          error={errors.vehicle_type}
          required
          placeholder="Select vehicle type"
        />
        
        <CommonInput
          label="Capacity (in tons)"
          id="capacity"
          type="number"
          value={formData.capacity}
          onChange={(e) => handleChange('capacity', e.target.value)}
          error={errors.capacity}
          required
          placeholder="Enter capacity"
        />
        
        {showTransporterDropdown && (
          <CommonSelect
            label="Transporter"
            id="transporter_id"
            value={formData.transporter_id}
            onValueChange={(value) => handleChange('transporter_id', value)}
            options={transporterOptions}
            error={errors.transporter_id}
            required
            placeholder="Select transporter"
            loading={loading.transporters}
            searchable
          />
        )}
      </div>
      
      <div className="flex justify-end space-x-3 pt-5">
        <CommonButton type="button" variant="outline" onClick={onCancel}>
          Cancel
        </CommonButton>
        <CommonButton type="submit" variant="default" loading={isSubmitting}>
          Add Vehicle
        </CommonButton>
      </div>
    </form>
  );
};

export default AddVehicleForm;